/**
 * Self-serve sharing with named people outside Joveo.
 *
 * Spec: docs/superpowers/specs/2026-10-12-self-serve-external-sharing-design.md §6
 *
 * A Share button for the Studio and docs headers. Any Joveo user with a
 * project open can share it, read-only, by email; what the person sees stays
 * the project's External view. The API (routes/project_external_members.py)
 * decides everything — this renders what it says, and the audience the
 * project list already derived (audience.tsx), never a guess of its own.
 */
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, Building2, Check, ChevronRight, Copy, Eye, Globe, Link2, Lock, UserPlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { authFetch } from "../utils/authFetch";
import { useAuth } from "../contexts/AuthContext";
import { useProject } from "../contexts/ProjectContext";
import { useShare } from "../contexts/ShareContext";
import { usePreview } from "../contexts/PreviewContext";
import { audienceOf, describePartnerReach, describePersonReach, supportsLinkSharing,
         type Audience, type PartnerReach, type PersonReach } from "./audience";

// ── API shapes ───────────────────────────────────────────────────────

export type MemberStatus = "active" | "invited" | "expired" | "blocked";

export interface ExternalMember {
  id: string;
  email: string | null;
  name: string | null;
  status: MemberStatus;
  reason: string | null;
  expiresAt: string | null;
  partners: string[];
  addedBy: string | null;
  addedById: string;
  createdAt: string;
  canRemove: boolean;
}

export type ShareOutcome =
  | "invited" | "shared" | "blocked" | "already" | "already_invited"
  | "joveo" | "personal" | "not_allowed" | "invalid";

export interface ShareResult {
  email: string;
  outcome: ShareOutcome;
  reason?: string;
  expiresAt?: string;
  emailSent?: boolean;
  /** Present when the email didn't go out: the project link to pass on. */
  link?: string;
}

interface MembersResponse {
  defaultScope: number;
  /** External view rules whose section no longer exists. */
  staleDefaultScope?: number;
  members: ExternalMember[];
  /** Everyone outside Joveo who can open it, named or not — the Members
   *  tab's answer, cut to what this dialog shows. */
  whoCanOpen?: OutsideView;
}

interface PartnerRef { id: string; name: string }

export interface Outsider {
  id: string;
  email: string;
  name: string | null;
  pending: boolean;
  via: PersonReach["via"];
  /** A named share's other way in, if removing it would leave one. */
  alsoVia?: "own" | "partner" | "enable" | null;
  /** The partners that let them in (names; ids in the same order). */
  partners: string[];
  partnerIds: string[];
  /** Their partners that can open it — on a Public project, all of them. */
  memberOf: PartnerRef[];
  mode: PersonReach["mode"];
  ruleCount: number;
}

export interface OutsideView {
  partners: Array<PartnerRef & { via: PartnerReach["via"]; mode: PartnerReach["mode"]; ruleCount: number }>;
  outside: Outsider[];
  shareLinks: number;
  integrations: number;
}

export interface SharableProject {
  id: string;
  slug: string;
  name: string;
  isPublished?: boolean;
  audience?: Audience;
  partner?: { id: string; slug: string; name: string } | null;
}

type Tone = "ok" | "info" | "neutral" | "warn" | "bad";

const TONE_TEXT: Record<Tone, string> = {
  ok: "bg-emerald-50 text-emerald-700",
  info: "bg-indigo-50 text-indigo-700",
  neutral: "bg-slate-100 text-slate-600",
  warn: "bg-amber-50 text-amber-800",
  bad: "bg-red-50 text-red-700",
};

// ── pure helpers (tested) ────────────────────────────────────────────

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** What to say about one address after Share. Every outcome the API returns
 *  has a line, and a missing email says so rather than claiming it was sent. */
export function outcomeText(r: ShareResult): { text: string; tone: Tone } {
  switch (r.outcome) {
    case "invited":
      return r.emailSent === false
        ? { text: "Invite created, but the email was not sent. Send them the project link; signing in with this address lets them in.", tone: "warn" }
        : { text: "Invite sent. It expires in 15 days.", tone: "ok" };
    case "shared":
      return r.emailSent === false
        ? { text: "Shared, but the email was not sent. Send them the project link.", tone: "warn" }
        : { text: "Shared. They have been notified.", tone: "ok" };
    case "blocked":
      return { text: `Blocked. ${r.reason ?? "They can't open this project."}`, tone: "warn" };
    case "already":
      return { text: "Already had access.", tone: "neutral" };
    case "already_invited":
      return { text: "Already invited.", tone: "neutral" };
    case "joveo":
      return { text: "Joveo address. Add them from Members instead.", tone: "bad" };
    case "personal":
      return { text: "Personal email addresses can't sign in to Cortex. Use their work address.", tone: "bad" };
    case "not_allowed":
      return { text: "This account can't be shared with.", tone: "bad" };
    case "invalid":
      return { text: "Not an email address.", tone: "bad" };
  }
}

export function memberChip(m: Pick<ExternalMember, "status" | "expiresAt">): { label: string; tone: Tone } {
  switch (m.status) {
    case "active": return { label: "Has access", tone: "ok" };
    case "invited":
      return { label: m.expiresAt ? `Invited · expires ${shortDate(m.expiresAt)}` : "Invited", tone: "info" };
    case "expired": return { label: "Invite expired", tone: "neutral" };
    case "blocked": return { label: "Blocked", tone: "warn" };
  }
}

/** Where the share form is offered. The same rule the API's share_refusal
 *  applies, from the same derived audience — so the dialog never offers a
 *  form the server will 409. */
export function canShareHere(p: SharableProject): boolean {
  return supportsLinkSharing(p) && p.isPublished !== false;
}

export interface PartnerGroup {
  id: string;
  name: string;
  /** The partner's own grant; absent when the group only gathers a Public
   *  project's readers by the partner they belong to. */
  access?: OutsideView["partners"][number];
  people: Outsider[];
}

/** Who can open it, one row per partner so a big partner stays one line:
 *  each person goes under the partners that let them in, or on a Public
 *  project the partners they belong to. Whoever is left is `others`, minus
 *  named shares, which have rows of their own. */
export function byPartner(w: Pick<OutsideView, "partners" | "outside">, named: Set<string>):
    { groups: PartnerGroup[]; others: Outsider[] } {
  // Keyed by id: two partners may share a name.
  const groups = new Map<string, PartnerGroup>(
    w.partners.map(p => [p.id, { id: p.id, name: p.name, access: p, people: [] }]));
  const others: Outsider[] = [];
  for (const o of w.outside) {
    // `partners` holds only rule and External-view grants; a partner let in
    // through Enable is still one of theirs that can open it.
    const under: PartnerRef[] = o.partnerIds.length
      ? o.partnerIds.map((id, i) => ({ id, name: o.partners[i] }))
      : o.memberOf.filter(m => o.via === "everyone" || groups.get(m.id)?.access);
    if (!under.length) {
      if (!named.has(o.email.toLowerCase())) others.push(o);
      continue;
    }
    for (const { id, name } of under) {
      if (!groups.has(id)) groups.set(id, { id, name, people: [] });
      groups.get(id)!.people.push(o);
    }
  }
  return { groups: [...groups.values()].sort((a, b) => a.name.localeCompare(b.name)), others };
}

const people = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

function initials(m: ExternalMember): string {
  const src = (m.name || m.email || "?").trim();
  const parts = m.name ? src.split(/\s+/) : [src.split("@")[0]];
  return parts.slice(0, 2).map(p => p[0] ?? "").join("").toUpperCase() || "?";
}

async function errorText(r: Response, fallback: string): Promise<string> {
  const d = await r.json().catch(() => ({}));
  if (typeof d.detail === "string") return d.detail;
  if (d.detail?.error) return d.detail.error;
  return fallback;
}

// ── the header button ────────────────────────────────────────────────

/** The project the page is showing. ProjectContext.currentProject is
 *  restored from localStorage and only synced to the URL once the page's own
 *  provider runs, so on a deep link it can still be the project used last —
 *  and the header would share THAT. The route's project wins; until it is in
 *  the list there is nothing to share. */
export function useProjectOnScreen() {
  const { projectSlug: urlSlug } = useParams<{ projectSlug?: string }>();
  const { currentProject, projects } = useProject();
  if (!urlSlug) return currentProject;
  if (currentProject && (currentProject.slug === urlSlug
      || currentProject.slug.startsWith(`${urlSlug}/`))) return currentProject;
  return projects.find(p => p.slug === urlSlug) ?? null;
}

export function ShareProjectButton({ labelFrom = "xl" }: {
  /** Breakpoint at which the "Share" label shows beside the icon. Studio's
   *  header is a tighter five-column grid, so it passes "2xl". */
  labelFrom?: "xl" | "2xl";
} = {}) {
  const { user } = useAuth();
  const { isAdmin, refreshProjects } = useProject();
  const currentProject = useProjectOnScreen();
  const share = useShare();
  const { isLocalPreview } = usePreview();
  const [open, setOpen] = useState(false);

  // Joveo people only (an impersonating super admin carries the target's
  // type), with a project on screen, outside a share-link session and the
  // local CLI preview — the API refuses everyone else anyway.
  if (user?.userType !== "internal" || !currentProject || share || isLocalPreview) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* A real trigger, so closing the dialog hands focus back to it. */}
      <DialogTrigger asChild>
        <button type="button" aria-label="Share project"
          className={`flex items-center gap-1.5 h-8 px-2 rounded-lg border border-indigo-200 bg-white text-indigo-700 text-[13px] font-medium hover:bg-indigo-50 transition-colors shrink-0 ${labelFrom === "2xl" ? "2xl:px-3" : "xl:px-3"}`}>
          <UserPlus className="size-4" />
          <span className={labelFrom === "2xl" ? "hidden 2xl:inline" : "hidden xl:inline"}>Share</span>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        {open && (
          <ShareDialogBody
            key={`${currentProject.id}:${currentProject.audience ?? ""}`}
            project={currentProject as SharableProject}
            isAdmin={isAdmin}
            currentUserId={user.id}
            onOpened={refreshProjects}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── the dialog body ──────────────────────────────────────────────────

export function ShareDialogBody({ project, isAdmin, currentUserId, onOpened }: {
  project: SharableProject;
  isAdmin: boolean;
  currentUserId: string;
  onOpened: () => Promise<void> | void;
}) {
  const audience = audienceOf(project);
  const sharable = canShareHere(project);
  // A Partner project takes no shares, but who can open it is still worth seeing.
  const listed = sharable || audience === "partner";
  const base = `/api/projects/${project.slug}/external-members`;

  const [data, setData] = useState<MembersResponse | null>(null);
  const [loadError, setLoadError] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState<ShareResult[]>([]);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const byPartnerId = useId();

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const r = await authFetch(base);
      if (!r.ok) { setLoadError(await errorText(r, "Could not load who this is shared with.")); return; }
      setData(await r.json());
    } catch {
      setLoadError("Could not load who this is shared with.");
    }
  }, [base]);

  useEffect(() => { if (listed) load(); }, [listed, load]);

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true); setError("");
    try {
      const r = await authFetch(base, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: [text] }),
      });
      if (!r.ok) { setError(await errorText(r, "Sharing failed.")); return; }
      const d = await r.json();
      setResults(d.results ?? []);
      setText("");
    } catch {
      setError("Network error. Nothing was shared.");
      return;
    } finally {
      setBusy(false);
    }
    // Outside the try: the share succeeded, so a failed refresh must not
    // claim otherwise (load reports its own error).
    void load();
  };

  const remove = async (m: ExternalMember) => {
    if (removing) return;
    setRemoving(true); setError("");
    try {
      const r = await authFetch(`${base}/${m.id}`, { method: "DELETE" });
      if (!r.ok) { setError(await errorText(r, "Could not remove them.")); return; }
      void load();
    } catch {
      setError("Network error. They were not removed.");
    } finally {
      setRemoving(false);
      setConfirming(null);
    }
  };

  const w = data?.whoCanOpen;
  // Only a row that stands for access hides its person from Others; an
  // expired invite's row must not hide someone who got in another way.
  const named = useMemo(() => new Set((data?.members ?? [])
    .filter(m => m.status === "active" || m.status === "invited")
    .map(m => (m.email ?? "").toLowerCase())), [data?.members]);
  const grouped = useMemo(() => w && byPartner(w, named), [w, named]);
  /** What removing a named share leaves them, as the server worked it out. */
  const afterRemoval = (m: ExternalMember) => {
    const o = w?.outside.find(x => x.email.toLowerCase() === (m.email ?? "").toLowerCase());
    if (o?.via === "everyone") return "This project is Public, so they can still read it.";
    if (o?.alsoVia === "partner") return `They keep access through ${o.partners.join(", ")}.`;
    if (o?.alsoVia === "own") return "They keep their own access, set by an admin.";
    if (o?.alsoVia === "enable") return "They keep access through Enable.";
    return "They lose access straight away.";
  };

  const copyLink = async (email: string, link: string) => {
    try { await navigator.clipboard.writeText(link); setCopied(email); setTimeout(() => setCopied(null), 1800); }
    catch { /* the link is shown below the result, selectable, to copy by hand */ }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Share “{project.name}”</DialogTitle>
        <DialogDescription>People outside Joveo get read-only access.</DialogDescription>
      </DialogHeader>

      {sharable && (
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="share-emails" className="block text-xs font-semibold text-slate-700">Email addresses</label>
            <div className="flex gap-2 items-start">
              <textarea id="share-emails" rows={2} value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
                placeholder="name@company.com, another@company.com"
                className="flex-1 min-w-0 resize-y px-3 py-2 text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 focus:bg-white" />
              <button type="button" onClick={submit} disabled={busy || !text.trim()}
                className="h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-default">
                {busy ? "Sharing…" : "Share"}
              </button>
            </div>
            {error && (
              <div role="alert" className="flex items-start gap-1.5 text-xs text-red-700">
                <AlertCircle className="size-3.5 mt-0.5 shrink-0" />{error}
              </div>
            )}
            {audience === "public" && (
              <p className="flex items-start gap-1.5 text-xs text-slate-600">
                <Globe className="size-3.5 mt-0.5 shrink-0 text-slate-500" />
                Anyone with a Cortex account can already read this project. Sharing tells them about it, and invites anyone without an account.
              </p>
            )}
            {data && (data.defaultScope > 0 || !data.staleDefaultScope) && (
              <p className="flex items-start gap-1.5 text-xs text-slate-600">
                <Eye className="size-3.5 mt-0.5 shrink-0 text-slate-500" />
                {data.defaultScope === 0
                  ? "They'll see the whole project. Its External view doesn't narrow it."
                  : `They'll see ${data.defaultScope} ${data.defaultScope === 1 ? "section" : "sections"}, set in this project's External view.`}
              </p>
            )}
            {data && !!data.staleDefaultScope && (
              <p role="status" className="flex items-start gap-1.5 text-xs text-amber-800">
                <AlertCircle className="size-3.5 mt-0.5 shrink-0" />
                {data.defaultScope === 0
                  ? "The External view points only at sections that no longer exist, so people you share with will see nothing. Ask a project admin to update it."
                  : `${data.staleDefaultScope} External view ${data.staleDefaultScope === 1 ? "rule points" : "rules point"} at sections that no longer exist.`}
              </p>
            )}
          </div>

          {/* Always mounted: a live region that appears together with its
              content isn't announced, so the first share would be silent. */}
          <div aria-live="polite">
          {results.length > 0 && (
            <ul aria-label="Share results" className="rounded-lg border border-slate-200 bg-slate-50 py-1">
              {results.map(r => {
                const o = outcomeText(r);
                return (
                  <li key={r.email} className="flex items-start gap-2 px-3 py-1.5 text-xs">
                    <span className={`size-[18px] rounded-full flex items-center justify-center shrink-0 ${TONE_TEXT[o.tone]}`}>
                      {o.tone === "ok" || o.tone === "neutral"
                        ? <Check className="size-3" aria-hidden="true" />
                        : <AlertCircle className="size-3" aria-hidden="true" />}
                    </span>
                    <span className="min-w-0 break-words flex-1">
                      <span className="font-semibold text-slate-800">{r.email}</span>
                      <span className="text-slate-600"> · {o.text}</span>
                      {r.link && (
                        <input readOnly value={r.link} aria-label={`Project link for ${r.email}`}
                          onFocus={e => e.currentTarget.select()}
                          className="mt-1 block w-full rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-700" />
                      )}
                    </span>
                    {r.link && (
                      <button type="button" onClick={() => copyLink(r.email, r.link!)}
                        className="shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-md border border-slate-200 bg-white text-[11px] font-medium text-slate-700 hover:bg-slate-50">
                        {copied === r.email ? <Check className="size-3" aria-hidden="true" /> : <Copy className="size-3" aria-hidden="true" />}
                        {copied === r.email ? "Copied" : "Copy link"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          </div>
        </div>
      )}

      {!sharable && audience === "private" && (
        <PrivateNotice project={project} isAdmin={isAdmin} onOpened={onOpened} />
      )}

      {!sharable && audience === "partner" && (
        <Notice icon={<Building2 className="size-4" />}>
          This project belongs to <strong>{project.partner?.name ?? "a partner"}</strong>. Its admins manage who outside Joveo can read it.
        </Notice>
      )}

      {!sharable && (audience === "shared" || audience === "public") && (
        <Notice tone="warn" icon={<AlertCircle className="size-4" />}>
          Publish this project before sharing it outside Joveo. People outside only ever see published projects.
        </Notice>
      )}

      {listed && (
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] font-semibold text-slate-700">People outside Joveo</span>
            {data && <span className="text-xs text-slate-500">
              {w ? `${people(w.outside.length)} can open it` : people(data.members.length)}
            </span>}
          </div>
          {loadError && <p role="alert" className="text-xs text-red-700">{loadError}</p>}
          {!data && !loadError && <p className="text-xs text-slate-500 py-3 text-center">Loading…</p>}
          {data && data.members.length === 0 && !grouped?.groups.length && !grouped?.others.length && (
            <p className="text-[13px] text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-xl">Nobody outside Joveo yet.</p>
          )}
          {data && data.members.length > 0 && !!(grouped?.groups.length || grouped?.others.length) && (
            <p className="text-[11px] font-medium text-slate-500">Shared by name</p>
          )}
          {data && data.members.length > 0 && (
            <ul aria-label="Shared by name" className="border border-slate-200 rounded-xl divide-y divide-slate-100">
              {data.members.map(m => {
                const chip = memberChip(m);
                const who = m.addedById === currentUserId ? "you" : (m.addedBy || "a former colleague");
                return (
                  <li key={m.id} className="px-3 py-2.5">
                    {confirming === m.id ? (
                      <div className="flex items-center gap-2 flex-wrap rounded-lg border border-red-200 bg-red-50 px-2.5 py-2">
                        <span className="flex-1 min-w-[12rem] text-xs text-red-900">
                          Remove <strong>{m.email}</strong>? {afterRemoval(m)}
                        </span>
                        <button type="button" onClick={() => setConfirming(null)} autoFocus disabled={removing}
                          className="h-8 px-3 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700">Cancel</button>
                        <button type="button" onClick={() => remove(m)} disabled={removing}
                          className="h-8 px-3 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50">
                          {removing ? "Removing…" : "Remove"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2.5">
                        <span className="size-8 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-semibold flex items-center justify-center shrink-0">{initials(m)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[13px] font-medium text-slate-800 break-all">{m.name || m.email}</span>
                            {m.partners.map(p => (
                              <span key={p} className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 rounded px-1.5">
                                <Building2 className="size-3" aria-hidden="true" />{p}
                              </span>
                            ))}
                            <span className={`text-[11px] font-medium rounded px-1.5 ${TONE_TEXT[chip.tone]}`}>{chip.label}</span>
                          </div>
                          {m.name && <div className="text-xs text-slate-600 break-all">{m.email}</div>}
                          {m.reason && (
                            <div className={`text-xs ${m.status === "blocked" ? "text-amber-800" : "text-slate-600"}`}>{m.reason}</div>
                          )}
                          <div className="text-[11px] text-slate-500">Added by {who} · {shortDate(m.createdAt)}</div>
                        </div>
                        {m.canRemove && (
                          <button type="button" onClick={() => setConfirming(m.id)}
                            aria-label={`Remove ${m.email ?? "this person"}`}
                            className="size-8 rounded-md flex items-center justify-center text-slate-500 hover:text-red-600 hover:bg-red-50 shrink-0">
                            <X className="size-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {grouped && (grouped.groups.length > 0 || grouped.others.length > 0) && (
            <>
              <p id={byPartnerId} className="text-[11px] font-medium text-slate-500 pt-1">By partner</p>
              <div role="group" aria-labelledby={byPartnerId}
                className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                {grouped.groups.map(g => (
                  <Group key={g.id} title={g.name} count={g.people.length}
                    detail={g.access && describePartnerReach({ ...g.access, canRead: true, reason: null })}>
                    {g.people.length === 0
                      ? <p className="text-xs text-slate-500">No one from {g.name} yet.</p>
                      : g.people.map(o => <Person key={o.id} o={o} named={named.has(o.email.toLowerCase())} />)}
                  </Group>
                ))}
                {grouped.others.length > 0 && (
                  <Group title={grouped.groups.length ? "Not through a partner" : "People"}
                    count={grouped.others.length} icon={false}>
                    {grouped.others.map(o => <Person key={o.id} o={o} why />)}
                  </Group>
                )}
              </div>
            </>
          )}
          {w && (w.shareLinks > 0 || w.integrations > 0) && (
            <p className="flex items-start gap-1.5 text-xs text-slate-600">
              <Link2 className="size-3.5 mt-0.5 shrink-0 text-slate-500" />
              Also open to {[w.shareLinks && `${w.shareLinks} live share ${w.shareLinks === 1 ? "link" : "links"}`,
                             w.integrations && `${w.integrations} API ${w.integrations === 1 ? "integration" : "integrations"}`]
                .filter(Boolean).join(" and ")}.
            </p>
          )}
        </div>
      )}
    </>
  );
}

/** One collapsed row that opens to the people inside it. */
function Group({ title, count, detail, icon = true, children }: {
  title: string; count: number; detail?: string; icon?: boolean; children: React.ReactNode;
}) {
  return (
    <details className="group">
      <summary className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-3.5 text-slate-400 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
        {icon && <Building2 className="size-3.5 text-slate-500 shrink-0" aria-hidden="true" />}
        <span className="text-[13px] font-medium text-slate-800 truncate">{title}</span>
        <span className="text-xs text-slate-500 shrink-0">{people(count)}</span>
        {detail && <span className="text-xs text-slate-500 truncate flex-1 min-w-0 text-right">{detail}</span>}
      </summary>
      <div className="pl-9 pr-3 pb-2.5 space-y-1">{children}</div>
    </details>
  );
}

function Person({ o, why = false, named = false }: { o: Outsider; why?: boolean; named?: boolean }) {
  return (
    <div className="flex items-baseline gap-1.5 flex-wrap text-xs">
      <span className="font-medium text-slate-700 break-all">{o.name || o.email}</span>
      {o.name && <span className="text-slate-500 break-all">{o.email}</span>}
      {o.pending && <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 rounded">pending invite</span>}
      {named && <span className="text-[10px] text-indigo-700 bg-indigo-50 px-1.5 rounded">shared by name</span>}
      {/* On a Public project every reason is the same, and says nothing. */}
      {why && o.via !== "everyone" && (
        <span className="text-slate-500">· {describePersonReach({ ...o, canRead: true, reason: null })}</span>
      )}
    </div>
  );
}

function Notice({ icon, children, tone = "neutral" }: {
  icon: React.ReactNode; children: React.ReactNode; tone?: "neutral" | "warn";
}) {
  const cls = tone === "warn"
    ? "bg-amber-50 border-amber-200 text-amber-800"
    : "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <div className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[13px] ${cls}`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div>{children}</div>
    </div>
  );
}

function PrivateNotice({ project, isAdmin, onOpened }: {
  project: SharableProject; isAdmin: boolean; onOpened: () => Promise<void> | void;
}) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const open = async () => {
    setBusy(true); setError("");
    try {
      const r = await authFetch(`/api/admin/projects/${project.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visibility: "external" }),
      });
      if (!r.ok) { setError(await errorText(r, "Could not open the project.")); setConfirm(false); return; }
      await onOpened();
    } catch {
      setError("Network error. The project was not changed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <Notice icon={<Lock className="size-4" />}>
        {isAdmin
          ? "Only Joveo can read this project. As a project admin, you can open it to named people outside. Nobody gets in until someone shares it with them."
          : "Only Joveo can read this project. Ask a project admin to open it to named people outside."}
      </Notice>
      {error && (
        <div role="alert" className="flex items-start gap-1.5 text-xs text-red-700">
          <AlertCircle className="size-3.5 mt-0.5 shrink-0" />{error}
        </div>
      )}
      {isAdmin && !confirm && (
        <button type="button" onClick={() => setConfirm(true)}
          className="h-9 px-3.5 rounded-lg bg-indigo-600 text-white text-[13px] font-medium hover:bg-indigo-700">
          Open to named people outside
        </button>
      )}
      {isAdmin && confirm && (
        <div className="flex items-center gap-2 flex-wrap rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5">
          <span className="flex-1 min-w-[14rem] text-xs text-indigo-950">
            Change this project from <strong>Only Joveo</strong> to <strong>Named people outside</strong>?
          </span>
          <button type="button" onClick={() => setConfirm(false)} disabled={busy} autoFocus
            className="h-8 px-3 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700">Cancel</button>
          <button type="button" onClick={open} disabled={busy}
            className="h-8 px-3 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50">
            {busy ? "Opening…" : "Open project"}
          </button>
        </div>
      )}
    </div>
  );
}
